import axios from "axios";
import { AuthResponse } from "../../models/auth.model";

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Crear instancia global de Axios
const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});


// Variables y funciones auxiliares para manejar el estado de refresco de tokens
let isRefreshing = false;
let failedQueue: {
  resolve: (value: unknown) => void;
  reject: (reason?: any) => void;
}[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (token) {
      promise.resolve(token);
    } else {
      promise.reject(error);
    }
  });

  failedQueue = [];
};

export const storeTokens = (token: string, refreshToken: string) => {
  // elige un único storage para consistencia
  localStorage.setItem("token", token);
  localStorage.setItem("refreshToken", refreshToken);
  api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
};


export const getStoredTokens = () => {
  // Busca en sessionStorage y si no, en localStorage (o viceversa, pero consistente)
  const token =
    localStorage.getItem("token") ||
    sessionStorage.getItem("token") ||
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  const refreshToken =
    localStorage.getItem("refreshToken") ||
    sessionStorage.getItem("refreshToken") ||
    localStorage.getItem("refresh_token") ||
    sessionStorage.getItem("refresh_token");

  return { token, refreshToken };
};
export const clearStoredTokens = () => {
  ["token", "refreshToken", "access_token", "refresh_token"].forEach(k => {
    localStorage.removeItem(k);
    sessionStorage.removeItem(k);
  });
  delete api.defaults.headers.common["Authorization"];
};
export const refreshAccessToken = async (): Promise<AuthResponse> => {
  const { refreshToken } = getStoredTokens();

  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  try {

    const response = await axios.post(`${BASE_URL}/auth/refresh-token`, {
      refreshToken,
    });
    const { token, refreshToken: newRefreshToken } = response.data;
    storeTokens(token, newRefreshToken);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw new Error("Failed to refresh access token");
  }
};

// Función global para manejar errores
const handleApiError = (error: any) => {
  if (error.response) {
    console.error(
      "API Error:",
      error.response.data.message || error.response.statusText
    );
  } else if (error.request) {
    console.error("Network Error:", error.message);
  } else {
    console.error("Error:", error.message);
  }
  throw error; // Propaga el error si es necesario
};

// Configurar interceptores
api.interceptors.response.use(
  (response) => response, // Devuelve la respuesta si no hay errores
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { token } = await refreshAccessToken();
        processQueue(null, token);
        originalRequest.headers["Authorization"] = `Bearer ${token}`;
        return api(originalRequest);
      } catch (err) {
        processQueue(err, null);
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    handleApiError(error); // Manejo global de errores
    return Promise.reject(error); // Propaga el error para manejarlo en servicios específicos si es necesario
  }
);

api.interceptors.request.use((config) => {
  const { token } = getStoredTokens();
  if (token) {
    const h = (config.headers ?? {}) as any;
    if (!h["Authorization"]) {
      h["Authorization"] = `Bearer ${token}`;
    }
    config.headers = h;
  }
  return config;
});


export default api;
